import { useLayoutEffect } from 'react';
import { yearMonth, type YearMonth } from '@overseer/contracts';
import { currentYearMonth } from '@/lib/time';
import { readSession, type SessionSlot, writeSession } from '@/lib/use-session-state';

/**
 * The month this tab last looked at.
 *
 * Timesheet, payroll and the employee strip all show one month. The nav items
 * carry no query string, so without this each screen would fall back to
 * today when moving between them. The month still lives in the URL when a
 * link names it — a pasted `?year=2026&month=6` is still June. This is only
 * the default when the URL is silent.
 *
 * `sessionStorage`, not `localStorage`, so a link to "the timesheet" always
 * opens on the current month for a new tab rather than on whatever month a
 * previous visit left it at.
 */
export const REMEMBERED_MONTH_KEY = 'overseer.month';

const REMEMBERED_MONTH: SessionSlot<YearMonth | null> = {
  key: REMEMBERED_MONTH_KEY,
  fallback: null,
  parse: (raw) => {
    const parsed = yearMonth.safeParse(JSON.parse(raw) as unknown);
    return parsed.success ? parsed.data : undefined;
  },
  serialize: (value) => (value === null ? null : JSON.stringify(value)),
};

export const readRememberedMonth = (): YearMonth | null => readSession(REMEMBERED_MONTH);

export const writeRememberedMonth = (value: YearMonth): void =>
  writeSession(REMEMBERED_MONTH, value);

/** The month a screen opens on when the URL does not name one. */
export const defaultYearMonth = (): YearMonth => readRememberedMonth() ?? currentYearMonth();

/**
 * Remember the month on screen, so the next screen that does not name one
 * opens on the same month.
 *
 * Layout rather than paint: a click on a nav item in the same frame must see
 * what this screen just showed, not last page's month or nothing.
 */
export const useRememberedMonth = ({ year, month }: YearMonth): void => {
  useLayoutEffect(() => {
    writeRememberedMonth({ year, month });
  }, [year, month]);
};
